/**
 * Shared building blocks for the PUBLIC marketing site (landing / about /
 * products / product-details). Brand constants, the logo lockup, and a small
 * scroll-reveal motion wrapper live here so every public page stays consistent.
 */
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

// Scroll-reveal now runs on the shared GSAP motion layer (ui/motion), which — unlike
// the previous framer-motion version — genuinely honours prefers-reduced-motion.
// Re-exported here so every `import { Reveal } from './shared'` keeps working unchanged.
export { Reveal } from '../ui/motion';

// ---- Public-site navigation + company info (single source of truth) ----
export const SITE_NAV = [
  { label: 'Home', to: '/' },
  { label: 'Products', to: '/catalog' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'About', to: '/about' },
];

export const CONTACT = {
  phone: '+91-33-4064 6022',
  email: 'support@greenbaybio.com',
  address: '2B, Grant Lane, 3rd Floor, Room No. 304, Kolkata 700012, India',
  hoursLabel: 'Mon–Sat · 9:30am – 6:30pm',
};

// The six solution areas Greenbay organises its range around (from greenbaybio.com).
// Keyed to the inline BrandIcons in assets/brand.ts.
export const SOLUTIONS: { title: string; icon: string; desc: string }[] = [
  { title: 'Soil Improvement', icon: 'soil', desc: 'Restore soil biology and structure for a living, fertile root zone.' },
  { title: 'Optimizing Rhizosphere', icon: 'bio', desc: 'Enrich the microbial life around the roots where nutrient uptake happens.' },
  { title: 'Natural Fertilizers', icon: 'leaf', desc: 'Bio-based nutrition that feeds the crop without chemical loading.' },
  { title: 'Plant Strength', icon: 'plant', desc: 'Build the plant’s own resistance and vigour against stress.' },
  { title: 'Water Management', icon: 'water', desc: 'Improve moisture retention and water-use efficiency in the field.' },
  { title: 'Weather Monitor', icon: 'weather', desc: 'Smarter, weather-aware crop care through the season.' },
];

export type SocialName = 'facebook' | 'instagram' | 'linkedin' | 'youtube';

export const SOCIAL: { label: string; href: string; icon: SocialName }[] = [
  { label: 'Facebook', href: 'https://facebook.com', icon: 'facebook' },
  { label: 'Instagram', href: 'https://instagram.com', icon: 'instagram' },
  { label: 'LinkedIn', href: 'https://linkedin.com', icon: 'linkedin' },
  { label: 'YouTube', href: 'https://youtube.com', icon: 'youtube' },
];

// Brand/social glyphs as inline SVG paths — this lucide build ships no brand
// icons, so we render them ourselves (keeps the public footer self-contained).
const SOCIAL_PATHS: Record<SocialName, string> = {
  facebook:
    'M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.99 3.66 9.13 8.44 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.49-3.9 3.78-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99C18.34 21.13 22 16.99 22 12z',
  instagram:
    'M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.43-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.66-.67 1.07-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13-.67-.66-1.34-1.07-2.13-1.38-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zm0 10.16a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm6.41-11.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44z',
  linkedin:
    'M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z',
  youtube:
    'M23.5 6.2a3.02 3.02 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3.02 3.02 0 0 0 .5 6.2C0 8.08 0 12 0 12s0 3.92.5 5.8a3.02 3.02 0 0 0 2.12 2.14c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3.02 3.02 0 0 0 2.12-2.14C24 15.92 24 12 24 12s0-3.92-.5-5.8zM9.55 15.57V8.43L15.82 12l-6.27 3.57z',
};

/** Inline brand/social icon (lucide has no brand icons in this build). */
export function SocialIcon({ name, className }: { name: SocialName; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d={SOCIAL_PATHS[name]} />
    </svg>
  );
}

/**
 * The Green Bay wordmark lockup — matches the authenticated app header so the
 * brand reads identically across the public site and the logged-in app.
 */
export function SiteLogo({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/" className="flex shrink-0 items-center" aria-label="Green Bay Bio — home">
      <img src="/logo.png" alt="Green Bay Biotech International" className={`w-auto ${compact ? 'h-8' : 'h-9 sm:h-10'}`} />
    </Link>
  );
}

/**
 * Section eyebrow — a harvest dot + uppercase brand label (no pill background).
 * Matches the redesigned public site's section rhythm. Keeps the `{children}` API.
 */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.14em] text-brand-600 dark:text-brand-400">
      <span className="size-[7px] shrink-0 rounded-full bg-harvest-400" />
      {children}
    </span>
  );
}

